import { dpow, dexp } from "./dmath";
import { rivalActive } from "./market";
import { C, directions, finaleTitles, gardenPlants, goals as goalDefs, furniture } from "@bd/content";
import { WEEKS_PER_YEAR, yearOf } from "./calendar";
import { addDecision, DECISION_HANDLERS } from "./company";
import { netWorth } from "./production";
import { eraCostMult } from "./perks";
import { levelCap } from "./studio";
import { rng, digits } from "./util";
import type { Company, GameState, MarketGame } from "./state";
import { clamp, log, money } from "./util";
import { priceIndex } from "./calendar";

// ------------------------------------------------------------------ the market's numbers: rival sales, the league, the charts

/** Copies a market game has sold so far: real for your games, estimated from score, era and size for the rivals'. */
export function marketSold(state: GameState, g: MarketGame, week = state.week): number {
  if (state.companies.some((c) => c.id === g.publisher)) return g.sold;
  const rc = C.rival[g.publisher];
  const total = 28000 * dpow(Math.max(1, g.score) / 7, 2.5) * dpow(1.045, yearOf(g.week) - 1965) * (rc?.acquirer ? 3 : 1);
  return Math.round(total * (1 - dexp(-Math.max(0, week - g.week) / 40)));
}

export interface LeagueRow {
  id: string;
  name: string;
  company: boolean;
  revenue: number; // sales over the last 52 weeks, in today's dollars
  fans: number;
  awards: number;
  rank: number;
}

let leagueMemo: { key: string; rows: LeagueRow[] } | null = null;
/** Forget the league memo (every week, and every online quarter: see resetCaches). */
export function resetLeagueMemo(): void {
  leagueMemo = null;
}

/** The publisher league: everyone ranked by a year of sales. Worked out once per game week. */
export function league(state: GameState): LeagueRow[] {
  const key = `${state.seed}:${state.week}:${state.market.games.length}:${state.rivals.filter((r) => r.bust).length}`;
  if (leagueMemo?.key === key) return leagueMemo.rows;
  const rows = leagueRows(state);
  leagueMemo = { key, rows };
  return rows;
}

function leagueRows(state: GameState): LeagueRow[] {
  const rows: LeagueRow[] = [];
  // what a publisher keeps of a typical box price after the shops' cut
  const price = 7.5 * priceIndex(state.week) * 0.5;
  const awardsBy = (name: string) => (state.market.awards ?? []).filter((a) => a.publisher === name).length;
  for (const co of state.companies) rows.push({ id: co.id, name: co.name, company: true, revenue: co.ledger.slice(-WEEKS_PER_YEAR).reduce((s, w) => s + w.income, 0), fans: Math.round(co.fans), awards: co.stats.awards, rank: 0 });
  for (const rv of state.rivals) {
    const rc = C.rival[rv.id];
    if (!rivalActive(rv, rc, state.week)) continue;
    let copies = 0;
    for (const g of state.market.games) if (g.publisher === rv.id && state.week - g.week < WEEKS_PER_YEAR * 4) copies += marketSold(state, g) - marketSold(state, g, state.week - WEEKS_PER_YEAR);
    // their fans grow with their hits, like yours
    const hits = state.market.games.filter((g) => g.publisher === rv.id && g.score >= 8).length;
    rows.push({ id: rv.id, name: rc.name, company: false, revenue: Math.round(copies * price), fans: Math.round(rv.reputation * 300 + hits * 1500 * dpow(1.03, yearOf(state.week) - 1965)), awards: awardsBy(rc.name), rank: 0 });
  }
  rows.sort((a, b) => b.revenue - a.revenue);
  rows.forEach((r, i) => (r.rank = i + 1));
  return rows;
}

export function leagueRank(state: GameState, co: Company): { rank: number; of: number } {
  const l = league(state);
  return { rank: l.find((r) => r.id === co.id)?.rank ?? l.length, of: l.length };
}

/** This year's bestsellers: the ten games released in the last 12 months that have sold the most. */
export function charts(state: GameState): { game: MarketGame; sold: number; publisher: string; yours: boolean }[] {
  return state.market.games
    .filter((g) => state.week - g.week < WEEKS_PER_YEAR)
    .map((g) => ({ game: g, sold: marketSold(state, g), publisher: state.companies.find((c) => c.id === g.publisher)?.name ?? C.rival[g.publisher]?.name ?? "", yours: state.companies.some((c) => c.id === g.publisher) }))
    .sort((a, b) => b.sold - a.sold)
    .slice(0, 10);
}

// ------------------------------------------------------------------ yearly goals

export interface YearGoal {
  id: string;
  text: string;
  target: number;
  base: number;
  baseList?: string[];
  startedWeek: number;
  done: boolean;
}

function launchedSince(co: Company, week: number) {
  return co.products.filter((p) => p.launched && p.releaseWeek >= week);
}

function totalSold(co: Company): number {
  return co.products.reduce((s, p) => s + p.sold, 0);
}

/** Where a goal stands now (0..target), and whether it's met. Some only count at the end of the year. */
export function goalProgress(state: GameState, co: Company, g: YearGoal): { value: number; met: boolean; atYearEnd?: boolean } {
  const since = launchedSince(co, g.startedWeek);
  const big = new Set(["base", "edition", "spinoff", "license"]);
  switch (C.goal[g.id]?.metric) {
    case "bestScore": {
      const v = Math.max(0, ...since.map((p) => p.score));
      return { value: v, met: v >= g.target };
    }
    case "copiesYear": {
      const v = totalSold(co) - g.base;
      return { value: v, met: v >= g.target };
    }
    case "leagueRank": {
      const v = leagueRank(state, co).rank;
      return { value: v, met: v <= g.target, atYearEnd: true };
    }
    case "newAudience": {
      const v = since.some((p) => big.has(p.kind) && !(g.baseList ?? []).includes(p.design.audience)) ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "bigBox": {
      const v = since.some((p) => p.design.scope === "big" || p.design.scope === "epic") ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "researchDone": {
      const v = co.researched.length + co.unlocked.length - g.base;
      return { value: v, met: v >= g.target };
    }
    case "hires": {
      const v = co.staff.length - g.base;
      return { value: v, met: v >= g.target };
    }
    case "award": {
      const v = co.stats.awards - g.base;
      return { value: v, met: v >= 1 };
    }
    case "evergreen": {
      // a new one, released this year
      const v = since.some((p) => p.evergreen && !p.retired) ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "shop": {
      const v = (co.stores ?? []).length - g.base;
      return { value: v, met: v >= 1 };
    }
    case "followUp": {
      const v = since.some((p) => p.kind !== "base" && p.kind !== "license") ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "familyHit": {
      const v = since.some((p) => (p.design.audience === "family" || p.design.audience === "kids" || p.design.audience === "party") && p.score >= 8) ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "bigHit": {
      const v = since.some((p) => (p.design.scope === "big" || p.design.scope === "epic") && p.score >= 8) ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "supplement": {
      const v = since.some((p) => p.kind === "rpg_supplement" || (p.kind === "expansion" && p.design.audience === "roleplayers")) ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "cardSet": {
      const v = since.some((p) => p.kind === "card_set") ? 1 : 0;
      return { value: v, met: v >= 1 };
    }
    case "noFlop": {
      const games = since.filter((p) => big.has(p.kind));
      const v = games.length && !games.some((p) => p.score < 5) ? 1 : 0;
      return { value: v, met: v >= 1, atYearEnd: true };
    }
    default:
      return { value: 0, met: false };
  }
}

function setGoals(state: GameState, co: Company): void {
  const r = rng(state, `goals:${co.id}`);
  const year = yearOf(state.week);
  const last = new Set((co.goals ?? []).map((g) => g.id));
  const rank = leagueRank(state, co).rank;
  // copies sold over the last year by everything you sell, the same thing the goal counts
  const lastYear = co.copiesLastYear ?? 0;
  const room = levelCap(co) - co.staff.length;
  const hasEvergreen = co.products.some((p) => p.launched && p.evergreen && !p.retired);
  const ok = goalDefs.filter((d) => d.from <= year && !d.direction
    && !(d.metric === "shop" && co.level !== "studio" && co.level !== "hq")
    && !(d.metric === "leagueRank" && rank <= 1)
    && !(d.metric === "hires" && room < 2)
    && !(d.metric === "evergreen" && hasEvergreen && co.stats.released < 3));
  const fresh = ok.filter((d) => !last.has(d.id));
  const pool = fresh.length >= 3 ? fresh : ok;
  const picked: typeof goalDefs = [];
  while (picked.length < 3 && picked.length < pool.length) {
    const d = r.pick(pool.filter((x) => !picked.includes(x)));
    picked.push(d);
  }
  co.goals = picked.map((d) => {
    const target =
      d.metric === "bestScore" ? Math.round((7 + Math.min(1.5, (year - 1965) / 20)) * 2) / 2
      : d.metric === "copiesYear" ? Math.max(5000, Math.round((lastYear * 1.15) / 1000) * 1000)
      : d.metric === "leagueRank" ? Math.max(1, Math.min(rank - 1, 5))
      : d.metric === "researchDone" ? 2 + Math.floor(r.float(0, 2))
      : d.metric === "hires" ? Math.min(room, 8, Math.max(2, Math.round(co.staff.length * 0.25)))
      : 1;
    const base = d.metric === "copiesYear" ? totalSold(co) : d.metric === "researchDone" ? co.researched.length + co.unlocked.length : d.metric === "hires" ? co.staff.length : d.metric === "award" ? co.stats.awards : d.metric === "shop" ? (co.stores ?? []).length : 0;
    const baseList = d.metric === "newAudience" ? [...new Set(co.products.filter((p) => p.launched).map((p) => p.design.audience))] : undefined;
    // "the top 1 publishers" reads badly: at the top, say so
    const text = d.text.includes("top {n}") && target === 1 ? "Finish the year as the biggest publisher in the league" : d.text.replace("{n}", d.metric === "copiesYear" ? digits(target) : String(target));
    return { id: d.id, text, target, base, baseList, startedWeek: state.week, done: false };
  });
  // your direction adds a goal of its own every year
  const dir = co.direction ? goalDefs.find((d) => d.direction === co.direction) : undefined;
  // ...but only once you make the kind of game it asks for (no "new card set" goal before your first card line)
  const needs = co.direction ? DIRECTION_FORMAT[co.direction] : undefined;
  if (dir && (!needs || co.products.some((p) => p.launched && p.design.format === needs))) co.goals.push({ id: dir.id, text: dir.text, target: 1, base: 0, startedWeek: state.week, done: false });
}

function reward(state: GameState, co: Company, g: YearGoal): string {
  const rw = C.goal[g.id].reward;
  const parts: string[] = [];
  if (rw.research) {
    // research rewards keep up with research costs
    const n = Math.round(rw.research * eraCostMult(yearOf(state.week)));
    co.research += n;
    parts.push(`+${n} research`);
  }
  if (rw.cashShare) {
    const yearly = co.ledger.reduce((s, w) => s + w.income, 0);
    const cash = Math.round(Math.max(2000 * priceIndex(state.week), yearly * rw.cashShare));
    co.cash += cash;
    parts.push(`+${money(cash)}`);
  }
  if (rw.rep) {
    co.reputation = clamp(co.reputation + rw.rep, 0, 100);
    parts.push(`+${rw.rep} reputation`);
  }
  if (rw.fans) {
    // fan rewards grow with your fan base
    const n = Math.round(Math.max(rw.fans, co.fans * 0.04) / 100) * 100;
    co.fans += n;
    parts.push(`+${digits(n)} fans`);
  }
  return parts.join(", ");
}

function goalsWeek(state: GameState, co: Company): void {
  const woy = state.week % WEEKS_PER_YEAR;
  // goals for the year arrive in January, once you've released something
  if (woy === 0 && co.stats.released > 0) {
    for (const g of co.goals ?? []) if (!g.done) {
      const p = goalProgress(state, co, g);
      if (p.atYearEnd && p.met) {
        g.done = true;
        co.goalsMet = (co.goalsMet ?? 0) + 1;
        log(state, "goal", `Goal met: ${g.text}. ${reward(state, co, g)}.`, co.id);
      }
    }
    // every goal met last year: a clean sweep, and a trophy for the shelf
    const lastGoals = co.goals ?? [];
    if (lastGoals.length >= 3 && lastGoals.every((g) => g.done)) {
      const year = yearOf(state.week) - 1;
      co.trophies = [...(co.trophies ?? []), { year, name: `Clean sweep ${year}` }];
      const n = co.trophies.length;
      const unlocks = furniture.filter((f) => f.sweeps === n);
      log(state, "trophy", `A clean sweep for ${year}: every goal met. Trophy number ${n}${unlocks.length ? `, and a reward for the studio: the ${unlocks.map((f) => f.name.toLowerCase()).join(" and ")} (free in Furnish)` : ""}.`, co.id);
    }
    const total = co.products.reduce((s, p) => s + p.sold, 0);
    co.copiesLastYear = total - (co.copiesAtYearStart ?? total);
    co.copiesAtYearStart = total;
    setGoals(state, co);
    log(state, "goals", `New goals for ${yearOf(state.week)}: ${(co.goals ?? []).map((g) => g.text.toLowerCase()).join("; ")}.`, co.id);
    return;
  }
  for (const g of co.goals ?? []) {
    if (g.done) continue;
    const p = goalProgress(state, co, g);
    if (p.met && !p.atYearEnd) {
      g.done = true;
      co.goalsMet = (co.goalsMet ?? 0) + 1;
      log(state, "goal", `Goal met: ${g.text}. ${reward(state, co, g)}.`, co.id);
    }
  }
}

// ------------------------------------------------------------------ company direction (year three)

/** The format a direction's yearly goal needs you to be making. */
const DIRECTION_FORMAT: Record<string, string> = { story: "rpg_book", cards: "ccg" };

function offerDirection(state: GameState, co: Company): void {
  if (co.direction || co.pending.some((d) => d.kind === "direction")) return;
  const made: Record<string, number> = {};
  for (const p of co.products) if (p.launched) made[p.design.audience] = (made[p.design.audience] ?? 0) + 1;
  const top = Object.entries(made).sort((a, b) => b[1] - a[1])[0]?.[0] ?? "family";
  const def = top === "hobby" || top === "hardcore" || top === "collectors" ? "hobby" : top === "roleplayers" ? "story" : "family";
  addDecision(state, co, {
    kind: "direction",
    title: "What kind of publisher are you?",
    text: "Three years in, shops and players are starting to know you for something. Pick the direction you want to be known for. It lasts the whole season.",
    options: directions.map((d) => {
      const f = DIRECTION_FORMAT[d.id] ? C.format[DIRECTION_FORMAT[d.id]] : undefined;
      const later = f && f.year > yearOf(state.week) ? ` (${f.name.toLowerCase()}s arrive around ${f.year})` : "";
      return { id: d.id, label: d.name, detail: d.blurb + later };
    }),
    defaultOption: def,
    weeks: 8,
  });
}

DECISION_HANDLERS.direction = (state, co, _d, option) => {
  const dir = C.direction[option];
  if (!dir) return;
  co.direction = dir.id;
  log(state, "direction", `${co.name} sets out to be known for ${dir.name.toLowerCase()}.`, co.id);
};

// ------------------------------------------------------------------ the 2035 finale

export interface FinaleAward {
  id: string;
  title: string;
  why: string;
  winner: string;
  winnerName: string;
  yours: boolean;
  value: string;
  /** the top three, so you see who it was close with */
  nominees: { id: string; name: string; yours: boolean; value: string }[];
  /** more than one publisher had a claim to it; otherwise it's a personal record */
  contested: boolean;
}

export interface Finale {
  week: number;
  awards: FinaleAward[];
  /** everyone still in business: companies by net worth, rivals by an estimate from their cash and a year of sales */
  ranking: { id: string; name: string; worth: number; company: boolean }[];
}

function companyStat(state: GameState, co: Company, stat: string): number {
  const launched = co.products.filter((p) => p.launched);
  switch (stat) {
    case "hits": return co.stats.hits;
    case "avgScore": return launched.length ? launched.reduce((s, p) => s + p.score, 0) / launched.length : 0;
    case "released": return co.stats.released;
    case "hitRate": return co.stats.released >= 10 ? (co.stats.hits / co.stats.released) * 100 : 0;
    // how long an evergreen has kept selling (not retired and still selling now)
    case "evergreenWeeks": return Math.max(0, ...launched.filter((p) => p.evergreen && !p.retired && (p.weekly.at(-1) ?? 0) > 0).map((p) => state.week - p.releaseWeek));
    case "misprints": return co.stats.misprints ?? state.log.filter((e) => e.company === co.id && (e.kind === "misprint" || e.kind === "shipping")).length;
    case "pets": return co.pets.length;
    case "worstBigScore": {
      const big = launched.filter((p) => p.design.scope === "big" || p.design.scope === "epic");
      return big.length ? 10 - Math.min(...big.map((p) => p.score)) : 0;
    }
    // went bankrupt and still finished as the richest company
    case "comeback": {
      if (co.stats.bankruptcies === 0) return 0;
      const best = Math.max(...state.companies.map((c) => netWorth(c, state.week)));
      return netWorth(co, state.week) >= best ? co.stats.bankruptcies : 0;
    }
    case "stores": return (co.stores ?? []).length;
    case "acquired": return state.rivals.filter((r) => r.acquiredBy === co.id).length;
    case "plants": {
      let n = 0;
      for (const r of co.rooms) if (r.type === "rooftop_garden") for (const f of r.furniture) if (f.kind === "planter") {
        let h = 0;
        for (const ch of f.id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
        const plant = gardenPlants[h % gardenPlants.length];
        if (plant && state.week - (r.builtWeek ?? 0) >= plant.weeks) n++;
      }
      return n;
    }
    case "awards": return co.stats.awards;
    default: return 0;
  }
}

function rivalStat(state: GameState, rivalId: string, stat: string): number {
  const games = state.market.games.filter((g) => g.publisher === rivalId);
  const rv = state.rivals.find((r) => r.id === rivalId);
  switch (stat) {
    case "hits": return games.filter((g) => g.score >= 8).length;
    case "avgScore": return games.length ? games.reduce((s, g) => s + g.score, 0) / games.length : 0;
    case "released": return rv?.released ?? 0;
    case "hitRate": return games.length >= 10 ? (games.filter((g) => g.score >= 8).length / games.length) * 100 : 0;
    case "awards": return (state.market.awards ?? []).filter((a) => a.publisher === C.rival[rivalId]?.name).length;
    default: return 0;
  }
}

function fmtStat(stat: string, v: number): string {
  if (stat === "avgScore") return v.toFixed(1);
  if (stat === "hitRate") return `${Math.round(v)}% hits`;
  if (stat === "evergreenWeeks") return `${Math.round(v / WEEKS_PER_YEAR)} years`;
  if (stat === "worstBigScore") return (10 - v).toFixed(1);
  if (stat === "comeback") return `after ${Math.round(v)} bankruptc${v === 1 ? "y" : "ies"}`;
  return String(Math.round(v));
}

/** Work out the finale: every title goes to whoever tops its stat, you and the rivals alike. */
export function computeFinale(state: GameState): Finale {
  const awards: FinaleAward[] = [];
  for (const t of finaleTitles) {
    const cands: { id: string; name: string; yours: boolean; v: number }[] = [];
    for (const co of state.companies) cands.push({ id: co.id, name: co.name, yours: true, v: companyStat(state, co, t.stat) });
    for (const rv of state.rivals) cands.push({ id: rv.id, name: C.rival[rv.id].name, yours: false, v: rivalStat(state, rv.id, t.stat) });
    const ranked = cands.filter((c) => c.v > 0).sort((a, b) => b.v - a.v);
    const best = ranked[0];
    if (best) awards.push({
      id: t.id, title: t.title, why: t.why, winner: best.id, winnerName: best.name, yours: best.yours, value: fmtStat(t.stat, best.v),
      nominees: ranked.slice(0, 3).map((c) => ({ id: c.id, name: c.name, yours: c.yours, value: fmtStat(t.stat, c.v) })),
      contested: ranked.length > 1,
    });
  }
  // contested titles first: they're the ones that were a race
  awards.sort((a, b) => Number(b.contested) - Number(a.contested));
  const rows = league(state);
  const ranking = [
    ...state.companies.map((co) => ({ id: co.id, name: co.name, worth: Math.round(netWorth(co, state.week)), company: true })),
    ...state.rivals.filter((rv) => rivalActive(rv, C.rival[rv.id], state.week)).map((rv) => ({ id: rv.id, name: C.rival[rv.id].name, worth: Math.round(Math.max(0, rv.cash) + (rows.find((r) => r.id === rv.id)?.revenue ?? 0) * 1.5), company: false })),
  ].sort((a, b) => b.worth - a.worth);
  return { week: state.week, awards, ranking };
}

export const goalsSystem = {
  name: "goals",
  week(state: GameState): void {
    for (const co of state.companies) {
      if (co.bankrupt) continue;
      goalsWeek(state, co);
      // year three, or the next new year for a save that was already past it
      if (state.week === WEEKS_PER_YEAR * 3 || (!co.direction && state.week > WEEKS_PER_YEAR * 3 && state.week % WEEKS_PER_YEAR === 0)) offerDirection(state, co);
    }
  },
};

export { yearOf };

/**
 * The Fan Board: what players rate highest right now, every publisher's games together. Reviews start it, how much
 * people still play a game moves it (your games' real replay; a rival's is guessed from its score), and age wears
 * it down slowly.
 */
export function fanBoard(state: GameState, limit = 25): { id: string; name: string; publisher: string; yours: string | null; rating: number; year: number }[] {
  const byMarket = new Map<string, { replay: number; evergreen: boolean; co: string }>();
  for (const co of state.companies) for (const p of co.products) if (p.launched && p.marketId) byMarket.set(p.marketId, { replay: p.trueReplay, evergreen: p.evergreen, co: co.id });
  const rows = state.market.games.map((g) => {
    const mine = byMarket.get(g.id);
    const replay = mine ? mine.replay : 0.5 + (g.score - 6) * 0.07;
    const age = (state.week - g.week) / WEEKS_PER_YEAR;
    const rating = clamp((g.score + (replay - 0.55) * 4 - age * (mine?.evergreen ? 0.03 : 0.12)) / 10, 0, 1) * 10;
    const pubName = state.companies.find((c) => c.id === g.publisher)?.name ?? C.rival[g.publisher]?.name ?? "?";
    return { id: g.id, name: g.name, publisher: pubName, yours: mine ? mine.co : null, rating: Math.round(rating * 10) / 10, year: yearOf(g.week) };
  });
  return rows.sort((a, b) => b.rating - a.rating).slice(0, limit);
}
